import {
  aggregationFns,
  type CellData,
  type ColumnFiltersState,
  type ColumnVisibilityState,
  columnFacetingFeature,
  columnFilteringFeature,
  columnGroupingFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createExpandedRowModel,
  createFilteredRowModel,
  createGroupedRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  type ExpandedState,
  filterFns,
  flexRender,
  type GroupingState,
  globalFilteringFeature,
  type PaginationState,
  type ReactTable,
  type RowData,
  rowAggregationFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowPinningFeature,
  rowSelectionFeature,
  rowSortingFeature,
  type SortingState,
  type StockFeatures,
  sortFns,
  type ColumnDef as TableColumnDef,
  type TableFeatures,
  type Row as TableRowInstance,
  type RowSelectionState as TableRowSelectionState,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  ArrowUp,
  ChevronRight,
  ChevronsUpDown,
  CircleHelp,
} from 'lucide-react';
import * as React from 'react';
import {
  Pagination,
  type PaginationShape,
  type PaginationSize,
  type PaginationType,
} from '@/components/molecules/navigation/Pagination';
import { Tooltip } from '@/components/organisms/overlays/Tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { dataTableTouchTargetClassName } from './DataTableHitArea';

// Preserve the capabilities of the previously exposed v8 table instance.
type DataTableFeatures = Omit<
  StockFeatures,
  'cellSelectionFeature' | 'cellSpanningFeature'
> & {
  filteredRowModel: ReturnType<typeof createFilteredRowModel>;
  groupedRowModel: ReturnType<typeof createGroupedRowModel>;
  sortedRowModel: ReturnType<typeof createSortedRowModel>;
  expandedRowModel: ReturnType<typeof createExpandedRowModel>;
  paginatedRowModel: ReturnType<typeof createPaginatedRowModel>;
  filterFns: typeof filterFns;
  sortFns: typeof sortFns;
  aggregationFns: typeof aggregationFns;
};
const dataTableFeatures: DataTableFeatures = tableFeatures({
  columnFacetingFeature,
  columnFilteringFeature,
  globalFilteringFeature,
  columnGroupingFeature,
  rowAggregationFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowPinningFeature,
  rowSelectionFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  groupedRowModel: createGroupedRowModel(),
  sortedRowModel: createSortedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  filterFns,
  sortFns,
  aggregationFns,
});

type ColumnDef<TData extends RowData, TValue = unknown> = TableColumnDef<
  DataTableFeatures,
  TData,
  TValue
>;
type Row<TData extends RowData> = TableRowInstance<DataTableFeatures, TData>;
type TanStackTable<TData extends RowData> = ReactTable<
  DataTableFeatures,
  TData
>;
type VisibilityState = ColumnVisibilityState;
type RowSelectionState = Record<string, boolean>;

function normalizeRowSelection(
  selection: RowSelectionState,
): TableRowSelectionState {
  return Object.fromEntries<true>(
    Object.keys(selection)
      .filter((id) => selection[id])
      .map((id) => [id, true]),
  );
}

export type ResponsiveColumnMeta = {
  /** Keep the column visible at all sizes, desktop only, or mobile only */
  priority?: 'always' | 'desktop' | 'mobile';
  /** Hide the column below the named container breakpoint */
  hideBelow?: 'sm' | 'md' | 'lg';
};

type ResponsiveColumnBreakpoint = NonNullable<
  ResponsiveColumnMeta['hideBelow']
>;
type ResponsiveColumnPriority = NonNullable<ResponsiveColumnMeta['priority']>;
type ResponsiveColumnVisibilitySlot = 'base' | ResponsiveColumnBreakpoint;
type ResponsiveStickySlots = {
  firstSlots: ResponsiveColumnVisibilitySlot[];
  lastSlots: ResponsiveColumnVisibilitySlot[];
};

const responsiveColumnBreakpointClasses: Record<
  ResponsiveColumnBreakpoint,
  string
> = {
  sm: '@sm:table-cell',
  md: '@md:table-cell',
  lg: '@lg:table-cell',
};

const responsiveColumnVisibilitySlots = [
  'base',
  'sm',
  'md',
  'lg',
] as const satisfies readonly ResponsiveColumnVisibilitySlot[];

const responsiveColumnVisibilityRanks: Record<
  ResponsiveColumnVisibilitySlot,
  number
> = {
  base: 0,
  sm: 1,
  md: 2,
  lg: 3,
};

declare module '@tanstack/react-table' {
  interface ColumnMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData,
    TValue extends CellData = CellData,
  > {
    /** Custom class name for header cell */
    headerClassName?: string;
    /** Custom class name for body cells */
    cellClassName?: string;
    /** Tooltip text shown via info icon next to header */
    headerTooltip?: string;
    /** Responsive visibility for table columns when DataTable responsiveColumns is enabled */
    responsive?: ResponsiveColumnMeta;
  }
}

/* ============================================
   DataTable Variants
   ============================================ */

const dataTableVariants = cva('w-full', {
  variants: {
    /** Visual style for row separation */
    dividers: {
      /** Border line between rows */
      line: '',
      /** Alternating row background fills */
      alternating: '',
    },
    /** Table density */
    density: {
      default: '',
      compact: '',
    },
  },
  defaultVariants: {
    dividers: 'line',
    density: 'default',
  },
});

const dataTableContainerVariants = cva('bg-card', {
  variants: {
    /** Border radius configuration */
    rounded: {
      /** Rounded on all corners (default) */
      all: 'rounded-xl',
      /** No rounding on any corner */
      none: 'rounded-none',
      /** Only top corners rounded */
      top: 'rounded-t-xl rounded-b-none',
      /** Only bottom corners rounded (for embedding in cards with headers) */
      bottom: 'rounded-t-none rounded-b-xl',
    },
    /** Whether to show the outer border */
    bordered: {
      true: 'border border-border shadow-xs',
      false: '',
    },
  },
  defaultVariants: {
    rounded: 'all',
    bordered: true,
  },
});

const dataTableRowVariants = cva(
  'transition-[background-color] duration-(--duration-base) ease-smooth motion-reduce:transition-none',
  {
    variants: {
      dividers: {
        line: 'border-b border-border-subtle',
        alternating: 'border-0',
      },
      density: {
        default: '',
        compact: '',
      },
      isEven: {
        true: '',
        false: '',
      },
      isSelected: {
        true: 'bg-muted dark:bg-accent shadow-[inset_3px_0_0_0_var(--color-primary)] rtl:shadow-[inset_-3px_0_0_0_var(--color-primary)]',
        false: '',
      },
    },
    compoundVariants: [
      // Alternating row colors (only when not selected)
      {
        dividers: 'alternating',
        isEven: true,
        isSelected: false,
        className: 'bg-muted',
      },
      {
        dividers: 'alternating',
        isEven: false,
        isSelected: false,
        className: 'bg-background dark:bg-card',
      },
    ],
    defaultVariants: {
      dividers: 'line',
      density: 'default',
      isEven: false,
      isSelected: false,
    },
  },
);

const dataTableCellVariants = cva('align-middle', {
  variants: {
    density: {
      default: 'h-[72px] ps-4 pe-4 py-3 @md:ps-6 @md:pe-6 @md:py-3',
      compact: 'h-14 ps-4 pe-4 py-2',
    },
  },
  defaultVariants: {
    density: 'default',
  },
});

const dataTableHeaderCellVariants = cva(
  'text-sm font-medium text-muted-foreground',
  {
    variants: {
      density: {
        default: 'h-11 ps-6 pe-6 py-3',
        compact: 'h-11 ps-4 pe-4 py-2',
      },
    },
    defaultVariants: {
      density: 'default',
    },
  },
);

function getStickyCellClass({
  stickyFirstColumn,
  stickyLastColumn,
  isFirstColumn,
  isLastColumn,
  isSelected,
}: {
  stickyFirstColumn: boolean;
  stickyLastColumn: boolean;
  isFirstColumn: boolean;
  isLastColumn: boolean;
  isSelected: boolean;
}): string | undefined {
  const isSticky =
    (stickyFirstColumn && isFirstColumn) || (stickyLastColumn && isLastColumn);

  if (!isSticky) {
    return undefined;
  }

  return cn(
    'sticky z-20',
    stickyFirstColumn && isFirstColumn && 'start-0',
    stickyLastColumn && isLastColumn && 'end-0',
    isSelected ? 'bg-muted dark:bg-accent' : 'bg-background dark:bg-card',
  );
}

function getResponsiveColumnPriority(
  priority: unknown,
): ResponsiveColumnPriority | undefined {
  switch (priority) {
    case 'always':
    case 'desktop':
    case 'mobile':
      return priority;
    default:
      return undefined;
  }
}

function getResponsiveColumnHideBelow(
  hideBelow: unknown,
): ResponsiveColumnBreakpoint | undefined {
  switch (hideBelow) {
    case 'sm':
    case 'md':
    case 'lg':
      return hideBelow;
    default:
      return undefined;
  }
}

function isColumnVisibleInResponsiveSlot(
  responsive: ResponsiveColumnMeta | undefined,
  enabled: boolean,
  slot: ResponsiveColumnVisibilitySlot,
): boolean {
  if (!enabled || !responsive) {
    return true;
  }

  const priority = getResponsiveColumnPriority(responsive.priority);
  if (priority === 'always') {
    return true;
  }

  if (priority === 'mobile') {
    return slot === 'base' || slot === 'sm';
  }

  const hideBelow = getResponsiveColumnHideBelow(responsive.hideBelow) ?? 'md';

  return (
    responsiveColumnVisibilityRanks[slot] >=
    responsiveColumnVisibilityRanks[hideBelow]
  );
}

function withResponsiveSlotVariant(
  slot: ResponsiveColumnVisibilitySlot,
  className: string,
): string {
  if (slot === 'base') {
    return className;
  }

  return className
    .split(' ')
    .map((token) => `@${slot}:${token}`)
    .join(' ');
}

function getResponsiveStickyStateClass({
  backgroundClass,
  side,
  slots,
  zIndexClass,
}: {
  backgroundClass: string;
  side: 'start' | 'end';
  slots: ResponsiveColumnVisibilitySlot[];
  zIndexClass: 'z-20' | 'z-30';
}): string | undefined {
  if (!slots.length) {
    return undefined;
  }

  const stickySlots = new Set(slots);
  const classes: string[] = [];
  let wasSticky = false;

  for (const slot of responsiveColumnVisibilitySlots) {
    const isSticky = stickySlots.has(slot);

    if (isSticky && !wasSticky) {
      classes.push(
        withResponsiveSlotVariant(
          slot,
          cn('sticky', zIndexClass, `${side}-0`, backgroundClass),
        ),
      );
    } else if (!isSticky && wasSticky) {
      classes.push(
        withResponsiveSlotVariant(slot, 'static z-auto start-auto end-auto'),
      );
    }

    wasSticky = isSticky;
  }

  return cn(classes);
}

function addResponsiveStickySlot<TItem>(
  slotMap: Map<string, ResponsiveStickySlots>,
  item: TItem | undefined,
  getId: (item: TItem) => string,
  slot: ResponsiveColumnVisibilitySlot,
  slotKey: keyof ResponsiveStickySlots,
): void {
  if (!item) {
    return;
  }

  const itemId = getId(item);
  const itemSlots = slotMap.get(itemId) ?? {
    firstSlots: [],
    lastSlots: [],
  };
  itemSlots[slotKey].push(slot);
  slotMap.set(itemId, itemSlots);
}

function getResponsiveStickySlotsById<TItem>({
  enabled,
  getId,
  getResponsive,
  items,
  stickyFirstColumn,
  stickyLastColumn,
}: {
  enabled: boolean;
  getId: (item: TItem) => string;
  getResponsive: (item: TItem) => ResponsiveColumnMeta | undefined;
  items: readonly TItem[];
  stickyFirstColumn: boolean;
  stickyLastColumn: boolean;
}): Map<string, ResponsiveStickySlots> {
  const slotMap = new Map<string, ResponsiveStickySlots>();

  if (!enabled || !(stickyFirstColumn || stickyLastColumn)) {
    return slotMap;
  }

  for (const slot of responsiveColumnVisibilitySlots) {
    const visibleItems = items.filter((item) =>
      isColumnVisibleInResponsiveSlot(getResponsive(item), enabled, slot),
    );

    if (stickyFirstColumn) {
      addResponsiveStickySlot(
        slotMap,
        visibleItems[0],
        getId,
        slot,
        'firstSlots',
      );
    }

    if (stickyLastColumn) {
      addResponsiveStickySlot(
        slotMap,
        visibleItems.at(-1),
        getId,
        slot,
        'lastSlots',
      );
    }
  }

  return slotMap;
}

function getResponsiveStickyClass({
  backgroundClass,
  firstSlots,
  lastSlots,
  zIndexClass = 'z-20',
}: {
  backgroundClass: string;
  firstSlots: ResponsiveColumnVisibilitySlot[];
  lastSlots: ResponsiveColumnVisibilitySlot[];
  zIndexClass?: 'z-20' | 'z-30';
}): string | undefined {
  return cn(
    getResponsiveStickyStateClass({
      backgroundClass,
      side: 'start',
      slots: firstSlots,
      zIndexClass,
    }),
    getResponsiveStickyStateClass({
      backgroundClass,
      side: 'end',
      slots: lastSlots,
      zIndexClass,
    }),
  );
}

function getHeaderCellWidth(size: number): number | undefined {
  if (size === 150) {
    return undefined;
  }

  return size;
}

function getAriaSort(
  sortState: false | 'asc' | 'desc',
): 'ascending' | 'descending' | undefined {
  if (sortState === 'asc') {
    return 'ascending';
  }

  if (sortState === 'desc') {
    return 'descending';
  }

  return undefined;
}

function getResponsiveColumnClass(
  responsive: ResponsiveColumnMeta | undefined,
  enabled: boolean,
): string | undefined {
  if (!enabled || !responsive) {
    return undefined;
  }

  const priority = getResponsiveColumnPriority(responsive.priority);

  if (priority === 'always') {
    return undefined;
  }

  if (priority === 'mobile') {
    return '@md:hidden';
  }

  const hideBelow = getResponsiveColumnHideBelow(responsive.hideBelow) ?? 'md';

  return `hidden ${responsiveColumnBreakpointClasses[hideBelow]}`;
}

function getVirtualRowStyle(
  start: number,
  headerOffset: number,
): React.CSSProperties {
  return {
    display: 'table',
    position: 'absolute',
    top: 0,
    insetInlineStart: 0,
    width: '100%',
    transform: `translateY(${start + headerOffset}px)`,
  };
}

/* ============================================
   Types
   ============================================ */

export interface DataTableProps<TData extends RowData, TValue>
  extends VariantProps<typeof dataTableVariants>,
    VariantProps<typeof dataTableContainerVariants> {
  /** Column definitions for the table */
  columns: ColumnDef<TData, TValue>[];
  /** Data to display in the table */
  data: TData[];
  /** Optional content rendered above the table, commonly DataTableToolbar */
  children?: React.ReactNode;

  /* ============================================
     Row Selection (Controlled & Uncontrolled)
     ============================================ */
  /** Enable row selection */
  enableRowSelection?: boolean;
  /** Enable multi-row selection */
  enableMultiRowSelection?: boolean;
  /** Controlled row selection state */
  rowSelection?: RowSelectionState;
  /** Callback when row selection changes (receives TanStack Updater) */
  onRowSelectionChange?: (
    updater:
      | RowSelectionState
      | ((old: RowSelectionState) => RowSelectionState),
  ) => void;
  /** Initial row selection state (uncontrolled) */
  initialRowSelection?: RowSelectionState;

  /* ============================================
     Sorting (Controlled & Uncontrolled)
     ============================================ */
  /** Enable sorting */
  enableSorting?: boolean;
  /** Sorting mode: 'client' = sort data locally, 'server' = fire callback only */
  sortingMode?: 'client' | 'server';
  /** Controlled sorting state */
  sorting?: SortingState;
  /** Callback when sorting changes (receives TanStack Updater) */
  onSortingChange?: (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => void;
  /** Initial sorting state (uncontrolled) */
  initialSorting?: SortingState;

  /* ============================================
     Pagination (Controlled & Uncontrolled)
     ============================================ */
  /** Enable pagination */
  enablePagination?: boolean;
  /** Pagination mode: 'client' = paginate data locally, 'server' = use pageCount, 'none' = no pagination UI */
  paginationMode?: 'client' | 'server' | 'none';
  /** Controlled pagination state */
  pagination?: PaginationState;
  /** Callback when pagination changes (receives TanStack Updater) */
  onPaginationChange?: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
  /** Total number of pages (for server mode) */
  pageCount?: number;
  /** Total number of rows (alternative to pageCount for server mode) */
  rowCount?: number;
  /** Page size for pagination (uncontrolled) */
  pageSize?: number;
  /** Initial page index (uncontrolled) */
  initialPageIndex?: number;
  /* ============================================
     Column Filters (Controlled & Uncontrolled)
     ============================================ */
  /** Enable column filters */
  enableColumnFilters?: boolean;
  /** Filtering mode: 'client' = filter data locally, 'server' = fire callback only */
  filteringMode?: 'client' | 'server';
  /** Controlled column filters state */
  columnFilters?: ColumnFiltersState;
  /** Callback when column filters change (receives TanStack Updater) */
  onColumnFiltersChange?: (
    updater:
      | ColumnFiltersState
      | ((old: ColumnFiltersState) => ColumnFiltersState),
  ) => void;
  /** Initial column filters state (uncontrolled) */
  initialColumnFilters?: ColumnFiltersState;

  /* ============================================
     Column Visibility (Controlled & Uncontrolled)
     ============================================ */
  /** Enable column visibility */
  enableColumnVisibility?: boolean;
  /** Controlled column visibility state */
  columnVisibility?: VisibilityState;
  /** Callback when column visibility changes (receives TanStack Updater) */
  onColumnVisibilityChange?: (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
  ) => void;
  /** Initial column visibility state (uncontrolled) */
  initialColumnVisibility?: VisibilityState;

  /* ============================================
     Grouping (Controlled & Uncontrolled)
     ============================================ */
  /** Enable grouping */
  enableGrouping?: boolean;
  /** Grouping mode: 'client' = group data locally, 'server' = data has subRows structure */
  groupingMode?: 'client' | 'server';
  /** Controlled grouping state */
  grouping?: GroupingState;
  /** Callback when grouping changes */
  onGroupingChange?: (
    updater: GroupingState | ((old: GroupingState) => GroupingState),
  ) => void;
  /** Initial grouping state (uncontrolled) */
  initialGrouping?: GroupingState;
  /** How grouped columns appear: 'remove' hides them, 'reorder' moves to start, false = no change */
  groupedColumnMode?: 'remove' | 'reorder' | false;

  /* ============================================
     Expansion (for grouped rows)
     ============================================ */
  /** Controlled expansion state */
  expanded?: ExpandedState;
  /** Callback when expansion changes */
  onExpandedChange?: (
    updater: ExpandedState | ((old: ExpandedState) => ExpandedState),
  ) => void;
  /** Initial expansion: true = all expanded, {} = all collapsed, or per-row object */
  initialExpanded?: ExpandedState;
  /** For server mode: extract subRows from data items */
  getSubRows?: (originalRow: TData, index: number) => TData[] | undefined;

  /* ============================================
     UI & Behavior
     ============================================ */
  /** Pagination variant type */
  paginationType?: PaginationType;
  /** Pagination shape */
  paginationShape?: PaginationShape;
  /** Pagination control size */
  paginationSize?: PaginationSize;
  /** Whether page-number pagination variants should render page buttons */
  paginationShowPageNumbers?: boolean;
  /** Accessible label for the pagination nav landmark (useful when multiple tables are on one page) */
  paginationLabel?: string;
  /** Get the row id for selection. Recommended for stable row identity; falls back to row index. */
  getRowId?: (row: TData, index: number, parent?: Row<TData>) => string;
  /** Callback when a row is clicked */
  onRowClick?: (row: Row<TData>) => void;
  /** Custom empty state content */
  emptyState?: React.ReactNode;
  /** Additional className */
  className?: string;
  /** Table caption for accessibility */
  caption?: string;
  /** Show header checkbox for select all */
  showSelectAll?: boolean;
  /** Apply column.meta.responsive visibility classes at container breakpoints */
  responsiveColumns?: boolean;
  /** Render function for the table instance (for external controls) */
  renderControls?: (table: TanStackTable<TData>) => React.ReactNode;
  /** Custom class names for table elements */
  classNames?: {
    table?: string;
    headerRow?: string;
    headerCell?: string;
    row?: string;
    cell?: string;
  };
  /** Custom render function for table body (replaces default body rendering) */
  renderBody?: (table: TanStackTable<TData>) => React.ReactNode;
  /** Custom render function for each row (replaces default row rendering) */
  renderRow?: (row: Row<TData>) => React.ReactNode;
  /** Custom render function for group header rows (when using grouping) */
  renderGroupHeader?: (
    row: Row<TData>,
    visibleColumnCount: number,
  ) => React.ReactNode;

  /* ============================================
     Scrolling & Sticky
     ============================================ */
  /** Maximum height for table body (enables vertical scrolling) */
  maxHeight?: string;
  /** Make table header sticky when scrolling */
  stickyHeader?: boolean;
  /** Make first column sticky when scrolling horizontally */
  stickyFirstColumn?: boolean;
  /** Make last column sticky when scrolling horizontally */
  stickyLastColumn?: boolean;

  /* ============================================
     Loading/Empty/Error States
     ============================================ */
  /** Show loading state */
  loading?: boolean;
  /** Custom loading state content */
  loadingState?: React.ReactNode;
  /** Error to display */
  error?: Error | null;
  /** Custom error state content */
  errorState?: React.ReactNode;

  /* ============================================
     Internationalization (i18n)
     ============================================ */
  /** Internationalization labels */
  i18n?: {
    /** Loading text (default: "Loading...") */
    loading?: string;
    /** Empty state text (default: "No results.") */
    noResults?: string;
    /** Selection count text (default: "N of M Row(s) Selected.") */
    selectionCount?: (selectedCount: number, totalCount: number) => string;
  };

  /* ============================================
     Virtualization (optional)
     ============================================ */
  /** Show "N of M Row(s) Selected." count in footer */
  showSelectionCount?: boolean;

  /** Virtualization configuration for large datasets */
  virtualization?: {
    /** Enable row virtualization */
    enabled: boolean;
    /** Estimated row height in pixels (default: 50) */
    estimatedRowHeight?: number;
    /** Estimated group header row height in pixels (default: 48) */
    estimatedGroupHeaderHeight?: number;
    /** Number of items to render outside visible area (default: 5) */
    overscan?: number;
  };
}

/* ============================================
   Selection Column Helper
   ============================================ */

const selectionCheckboxClassName =
  'translate-y-[2px] before:-inset-[14px] before:rounded-md';

export function createSelectColumn<TData extends RowData>(): ColumnDef<
  TData,
  unknown
> {
  return {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        indeterminate={
          table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected()
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className={selectionCheckboxClassName}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className={selectionCheckboxClassName}
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 64,
    meta: {
      headerClassName: 'w-16 min-w-16',
      cellClassName: 'w-16 min-w-16',
    },
  };
}

/* ============================================
   Sortable Header Component
   ============================================ */

interface SortableHeaderProps {
  children: React.ReactNode;
  column: {
    getIsSorted: () => false | 'asc' | 'desc';
    toggleSorting: (desc?: boolean) => void;
    getCanSort: () => boolean;
    columnDef?: { meta?: { headerTooltip?: string } };
  };
  className?: string;
}

function HeaderTooltip({
  content,
}: {
  content?: string;
}): React.JSX.Element | null {
  if (!content) {
    return null;
  }

  return (
    <Tooltip
      trigger={
        <button
          type="button"
          className={cn(
            dataTableTouchTargetClassName,
            'rounded-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          )}
          aria-label="Column information"
        >
          <CircleHelp className="size-3.5 shrink-0" />
        </button>
      }
      content={content}
    />
  );
}

export function SortableHeader({
  children,
  column,
  className,
}: SortableHeaderProps): React.JSX.Element {
  'use no memo'; // TanStack column identity stays stable while its sorting state changes.
  const sorted = column.getIsSorted();
  const canSort = column.getCanSort();
  const tooltipContent = column.columnDef?.meta?.headerTooltip;

  if (!canSort) {
    if (!tooltipContent) {
      return <span className={className}>{children}</span>;
    }

    return (
      <span className={cn('inline-flex items-center gap-1', className)}>
        <span>{children}</span>
        <HeaderTooltip content={tooltipContent} />
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <button
        type="button"
        onClick={() => column.toggleSorting(sorted === 'asc')}
        className={cn(
          dataTableTouchTargetClassName,
          'flex items-center gap-1 text-start hover:text-foreground transition-colors duration-(--duration-fast) ease-standard motion-reduce:transition-none -ms-1 ps-1 -me-1 pe-1 -my-0.5 py-0.5 rounded focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        )}
      >
        {children}
        <span className="relative size-4">
          <ArrowUp
            aria-hidden="true"
            className={cn(
              'absolute inset-0 size-4 transition-[opacity,rotate,color] duration-(--duration-base) ease-standard motion-reduce:transition-none',
              sorted
                ? 'opacity-100 text-foreground'
                : 'opacity-0 text-muted-foreground',
              sorted === 'desc' ? 'rotate-180' : 'rotate-0',
            )}
          />
          <ChevronsUpDown
            className={cn(
              'absolute inset-0 size-4 text-muted-foreground transition-opacity duration-(--duration-base) ease-standard motion-reduce:transition-none',
              sorted ? 'opacity-0' : 'opacity-100 text-muted-foreground',
            )}
          />
        </span>
      </button>
      <HeaderTooltip content={tooltipContent} />
    </span>
  );
}

/* ============================================
   useDataTable Hook (Headless)
   ============================================ */

export interface UseDataTableOptions<TData extends RowData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  // Row Selection
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (
    updater:
      | RowSelectionState
      | ((old: RowSelectionState) => RowSelectionState),
  ) => void;
  initialRowSelection?: RowSelectionState;
  showSelectAll?: boolean;
  // Sorting
  enableSorting?: boolean;
  sortingMode?: 'client' | 'server';
  sorting?: SortingState;
  onSortingChange?: (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => void;
  initialSorting?: SortingState;
  // Pagination
  enablePagination?: boolean;
  paginationMode?: 'client' | 'server' | 'none';
  pagination?: PaginationState;
  onPaginationChange?: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
  pageCount?: number;
  rowCount?: number;
  pageSize?: number;
  initialPageIndex?: number;
  // Column Filters
  enableColumnFilters?: boolean;
  filteringMode?: 'client' | 'server';
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (
    updater:
      | ColumnFiltersState
      | ((old: ColumnFiltersState) => ColumnFiltersState),
  ) => void;
  initialColumnFilters?: ColumnFiltersState;
  // Column Visibility
  enableColumnVisibility?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
  ) => void;
  initialColumnVisibility?: VisibilityState;
  // Grouping
  enableGrouping?: boolean;
  groupingMode?: 'client' | 'server';
  grouping?: GroupingState;
  onGroupingChange?: (
    updater: GroupingState | ((old: GroupingState) => GroupingState),
  ) => void;
  initialGrouping?: GroupingState;
  groupedColumnMode?: 'remove' | 'reorder' | false;
  // Expansion
  expanded?: ExpandedState;
  onExpandedChange?: (
    updater: ExpandedState | ((old: ExpandedState) => ExpandedState),
  ) => void;
  initialExpanded?: ExpandedState;
  // Other
  /** Get the row id. Recommended for stable row identity; falls back to row index. */
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  getSubRows?: (originalRow: TData, index: number) => TData[] | undefined;
}

interface DataTableGroupHeaderProps<TData extends RowData> {
  row: Row<TData>;
  visibleColumnCount: number;
  customHeader: (
    row: Row<TData>,
    visibleColumnCount: number,
  ) => React.ReactNode;
}

function DataTableGroupHeader<TData extends RowData>({
  row,
  visibleColumnCount,
  customHeader,
}: DataTableGroupHeaderProps<TData>) {
  return <>{customHeader(row, visibleColumnCount)}</>;
}

interface DataTableControlsProps<TData extends RowData> {
  table: TanStackTable<TData>;
  customControls: (table: TanStackTable<TData>) => React.ReactNode;
}

function DataTableControls<TData extends RowData>({
  table,
  customControls,
}: DataTableControlsProps<TData>) {
  return <>{customControls(table)}</>;
}

export interface UseDataTableResult<TData extends RowData> {
  table: TanStackTable<TData>;
  totalPages: number;
  currentPage: number;
  showPagination: boolean;
}

export function useDataTable<TData extends RowData, TValue>({
  columns,
  data,
  // Row Selection
  enableRowSelection = false,
  enableMultiRowSelection = true,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  initialRowSelection = {},
  showSelectAll = true,
  // Sorting
  enableSorting = true,
  sortingMode = 'client',
  sorting: controlledSorting,
  onSortingChange,
  initialSorting = [],
  // Pagination
  enablePagination = false,
  paginationMode = 'client',
  pagination: controlledPagination,
  onPaginationChange,
  pageCount: controlledPageCount,
  rowCount,
  pageSize = 10,
  initialPageIndex = 0,
  // Column Filters
  enableColumnFilters = false,
  filteringMode = 'client',
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  initialColumnFilters = [],
  // Column Visibility
  enableColumnVisibility = false,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  initialColumnVisibility = {},
  // Grouping
  enableGrouping = false,
  groupingMode = 'client',
  grouping: controlledGrouping,
  onGroupingChange,
  initialGrouping = [],
  groupedColumnMode = false,
  // Expansion
  expanded: controlledExpanded,
  onExpandedChange,
  initialExpanded = {},
  // Other
  getRowId,
  getSubRows,
}: UseDataTableOptions<TData, TValue>): UseDataTableResult<TData> {
  const defaultPaginationState: PaginationState = React.useMemo(
    () => ({
      pageIndex: initialPageIndex,
      pageSize,
    }),
    [initialPageIndex, pageSize],
  );

  const [internalRowSelection, setInternalRowSelection] =
    React.useState<RowSelectionState>(initialRowSelection);
  const [internalSorting, setInternalSorting] =
    React.useState<SortingState>(initialSorting);
  const [internalColumnFilters, setInternalColumnFilters] =
    React.useState<ColumnFiltersState>(initialColumnFilters);
  const [internalColumnVisibility, setInternalColumnVisibility] =
    React.useState<VisibilityState>(initialColumnVisibility);
  const [internalPagination, setInternalPagination] =
    React.useState<PaginationState>(defaultPaginationState);
  const [internalGrouping, setInternalGrouping] =
    React.useState<GroupingState>(initialGrouping);
  const [internalExpanded, setInternalExpanded] =
    React.useState<ExpandedState>(initialExpanded);

  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const sorting = controlledSorting ?? internalSorting;
  const columnFilters = controlledColumnFilters ?? internalColumnFilters;
  const columnVisibility =
    controlledColumnVisibility ?? internalColumnVisibility;
  const pagination = controlledPagination ?? internalPagination;
  const grouping = controlledGrouping ?? internalGrouping;
  const expanded = controlledExpanded ?? internalExpanded;

  const handleRowSelectionChange = React.useCallback(
    (
      updater:
        | TableRowSelectionState
        | ((old: TableRowSelectionState) => TableRowSelectionState),
    ) => {
      const translated =
        typeof updater === 'function'
          ? (old: RowSelectionState): RowSelectionState =>
              updater(normalizeRowSelection(old))
          : updater;
      if (controlledRowSelection === undefined) {
        setInternalRowSelection(translated);
      }
      onRowSelectionChange?.(translated);
    },
    [controlledRowSelection, onRowSelectionChange],
  );

  const handleSortingChange = React.useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      if (controlledSorting === undefined) {
        setInternalSorting(updater);
      }
      onSortingChange?.(updater);
    },
    [controlledSorting, onSortingChange],
  );

  const handleColumnFiltersChange = React.useCallback(
    (
      updater:
        | ColumnFiltersState
        | ((old: ColumnFiltersState) => ColumnFiltersState),
    ) => {
      if (controlledColumnFilters === undefined) {
        setInternalColumnFilters(updater);
      }
      onColumnFiltersChange?.(updater);
    },
    [controlledColumnFilters, onColumnFiltersChange],
  );

  const handleColumnVisibilityChange = React.useCallback(
    (
      updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
    ) => {
      if (controlledColumnVisibility === undefined) {
        setInternalColumnVisibility(updater);
      }
      onColumnVisibilityChange?.(updater);
    },
    [controlledColumnVisibility, onColumnVisibilityChange],
  );

  const handlePaginationChange = React.useCallback(
    (
      updater: PaginationState | ((old: PaginationState) => PaginationState),
    ) => {
      if (controlledPagination === undefined) {
        setInternalPagination(updater);
      }
      onPaginationChange?.(updater);
    },
    [controlledPagination, onPaginationChange],
  );

  const handleGroupingChange = React.useCallback(
    (updater: GroupingState | ((old: GroupingState) => GroupingState)) => {
      if (controlledGrouping === undefined) {
        setInternalGrouping(updater);
      }
      onGroupingChange?.(updater);
    },
    [controlledGrouping, onGroupingChange],
  );

  const handleExpandedChange = React.useCallback(
    (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => {
      if (controlledExpanded === undefined) {
        setInternalExpanded(updater);
      }
      onExpandedChange?.(updater);
    },
    [controlledExpanded, onExpandedChange],
  );

  const tableColumns = React.useMemo(() => {
    const helper = createColumnHelper<DataTableFeatures, TData>();
    return helper.columns<
      (ColumnDef<TData, TValue> | ColumnDef<TData, unknown>)[]
    >(
      enableRowSelection && showSelectAll
        ? [createSelectColumn<TData>(), ...columns]
        : [...columns],
    );
  }, [columns, enableRowSelection, showSelectAll]);

  const manualPagination = !enablePagination || paginationMode !== 'client';
  const manualSorting = !enableSorting || sortingMode !== 'client';
  const manualFiltering = !enableColumnFilters || filteringMode !== 'client';
  const manualGrouping = !enableGrouping || groupingMode !== 'client';

  const pageCount = React.useMemo(() => {
    if (controlledPageCount !== undefined) {
      return controlledPageCount;
    }
    if (rowCount !== undefined && pagination.pageSize > 0) {
      return Math.ceil(rowCount / pagination.pageSize);
    }
    return undefined;
  }, [controlledPageCount, rowCount, pagination.pageSize]);

  const table = useTable({
    features: dataTableFeatures,
    data,
    columns: tableColumns,
    state: {
      rowSelection: React.useMemo(
        () => normalizeRowSelection(rowSelection),
        [rowSelection],
      ),
      sorting,
      columnFilters,
      columnVisibility,
      pagination,
      grouping,
      expanded,
    },
    enableRowSelection,
    enableMultiRowSelection,
    enableRowRangeSelection: false,
    onRowSelectionChange: handleRowSelectionChange,
    enableSorting,
    manualSorting,
    onSortingChange: handleSortingChange,
    manualPagination,
    // Disabling the client model must not change v8's page-reset default.
    autoResetPageIndex: paginationMode !== 'server',
    pageCount,
    onPaginationChange: handlePaginationChange,
    enableColumnFilters,
    manualFiltering,
    onColumnFiltersChange: handleColumnFiltersChange,
    enableHiding: enableColumnVisibility,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    enableGrouping,
    manualGrouping,
    groupedColumnMode: groupedColumnMode || undefined,
    onGroupingChange: handleGroupingChange,
    onExpandedChange: handleExpandedChange,
    // Without a client pagination model, expansion must happen in the
    // expanded row model rather than being deferred to pagination.
    paginateExpandedRows: !enablePagination || paginationMode !== 'client',
    getSubRows: groupingMode === 'server' ? getSubRows : undefined,
    manualExpanding: !enableGrouping,
    getRowId,
  });

  return {
    table,
    totalPages: table.getPageCount(),
    currentPage: pagination.pageIndex + 1,
    showPagination:
      enablePagination && paginationMode !== 'none' && table.getPageCount() > 1,
  };
}

/* ============================================
   DataTable Component
   ============================================ */

function DataTable<TData extends RowData, TValue>({
  columns,
  data,
  children,
  // Row Selection
  enableRowSelection = false,
  enableMultiRowSelection = true,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  initialRowSelection = {},
  // Sorting
  enableSorting = true,
  sortingMode = 'client',
  sorting: controlledSorting,
  onSortingChange,
  initialSorting = [],
  // Pagination
  enablePagination = false,
  paginationMode = 'client',
  pagination: controlledPagination,
  onPaginationChange,
  pageCount: controlledPageCount,
  rowCount,
  pageSize = 10,
  initialPageIndex = 0,
  // Column Filters
  enableColumnFilters = false,
  filteringMode = 'client',
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  initialColumnFilters = [],
  // Column Visibility
  enableColumnVisibility = false,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  initialColumnVisibility = {},
  // Grouping
  enableGrouping = false,
  groupingMode = 'client',
  grouping: controlledGrouping,
  onGroupingChange,
  initialGrouping = [],
  groupedColumnMode = false,
  // Expansion
  expanded: controlledExpanded,
  onExpandedChange,
  initialExpanded = {},
  getSubRows,
  // UI & Behavior
  paginationType = 'page-default',
  paginationShape = 'square',
  paginationSize = 'md',
  paginationShowPageNumbers = true,
  paginationLabel,
  getRowId,
  onRowClick,
  emptyState,
  className,
  caption,
  dividers = 'line',
  density = 'default',
  showSelectAll = true,
  responsiveColumns = false,
  renderControls,
  classNames,
  renderBody,
  renderRow,
  renderGroupHeader,
  // Scrolling & Sticky
  maxHeight,
  stickyHeader = false,
  stickyFirstColumn = false,
  stickyLastColumn = false,
  // Loading/Empty/Error States
  loading: isLoading = false,
  loadingState,
  error,
  errorState,
  // Internationalization
  i18n,
  // Virtualization
  virtualization,
  // Selection count
  showSelectionCount = false,
  // Container styling
  rounded = 'all',
  bordered = true,
}: DataTableProps<TData, TValue>): React.JSX.Element {
  const { table, totalPages, currentPage, showPagination } = useDataTable({
    columns,
    data,
    enableRowSelection,
    enableMultiRowSelection,
    rowSelection: controlledRowSelection,
    onRowSelectionChange,
    initialRowSelection,
    showSelectAll,
    enableSorting,
    sortingMode,
    sorting: controlledSorting,
    onSortingChange,
    initialSorting,
    enablePagination,
    paginationMode,
    pagination: controlledPagination,
    onPaginationChange,
    pageCount: controlledPageCount,
    rowCount,
    pageSize,
    initialPageIndex,
    enableColumnFilters,
    filteringMode,
    columnFilters: controlledColumnFilters,
    onColumnFiltersChange,
    initialColumnFilters,
    enableColumnVisibility,
    columnVisibility: controlledColumnVisibility,
    onColumnVisibilityChange,
    initialColumnVisibility,
    // Grouping
    enableGrouping,
    groupingMode,
    grouping: controlledGrouping,
    onGroupingChange,
    initialGrouping,
    groupedColumnMode,
    // Expansion
    expanded: controlledExpanded,
    onExpandedChange,
    initialExpanded,
    getSubRows,
    getRowId,
  });

  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;
  const isVirtualizationEnabled = virtualization?.enabled ?? false;

  const headerOffset = stickyHeader && isVirtualizationEnabled ? 40 : 0;

  const isGroupRow = React.useCallback(
    (row: Row<TData>) => {
      if (row.getIsGrouped()) return true;
      if (groupingMode === 'server' && row.subRows.length > 0) return true;
      return false;
    },
    [groupingMode],
  );

  const rowVirtualizer = useVirtualizer({
    enabled: isVirtualizationEnabled,
    count: isVirtualizationEnabled ? rows.length : 0,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: (index) => {
      const row = rows[index];
      if (row && isGroupRow(row)) {
        return virtualization?.estimatedGroupHeaderHeight ?? 48;
      }
      return virtualization?.estimatedRowHeight ?? 50;
    },
    overscan: virtualization?.overscan ?? 5,
  });

  const handleRowKeyDown = React.useCallback(
    (row: Row<TData>, e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick?.(row);
      }
    },
    [onRowClick],
  );

  const renderCellContent = React.useCallback(
    (
      cell: ReturnType<Row<TData>['getVisibleCells']>[number],
      row: Row<TData>,
    ) => {
      const isGroupedCell = cell.getIsGrouped();
      const isPlaceholderCell = cell.getIsPlaceholder();
      // Preserve v8 presentation even when v9 has no resolved aggregation function.
      const isAggregatedCell =
        !isGroupedCell && !isPlaceholderCell && !!cell.row.subRows?.length;

      if (isGroupedCell) {
        const actionLabel = row.getIsExpanded() ? 'Collapse' : 'Expand';
        const groupLabel = String(cell.getValue() ?? '');

        return (
          <button
            type="button"
            onClick={row.getToggleExpandedHandler()}
            className="flex items-center gap-1.5 text-start font-medium group/expand rounded -ms-1 ps-1 -me-1 pe-1 -my-0.5 py-0.5 hover:text-foreground transition-colors duration-(--duration-fast) ease-standard motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            aria-label={`${actionLabel} ${groupLabel} group`}
            aria-expanded={row.getIsExpanded()}
          >
            <ChevronRight
              className={cn(
                'h-4 w-4 flex-shrink-0 transition-[rotate] duration-(--duration-base) ease-standard motion-reduce:transition-none',
                row.getIsExpanded() && 'rotate-90',
              )}
            />
            <span>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </span>
            <span className="text-secondary-foreground tabular-nums">
              ({row.subRows.length})
            </span>
          </button>
        );
      }

      if (isAggregatedCell) {
        return flexRender(
          cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
          cell.getContext(),
        );
      }

      if (isPlaceholderCell) {
        return null;
      }

      return flexRender(cell.column.columnDef.cell, cell.getContext());
    },
    [],
  );
  const hasChildren = React.Children.count(children) > 0;
  const hasFooter = showPagination || showSelectionCount;
  const shouldRenderComposite = hasChildren || hasFooter;
  const selectedRowCount = table.getFilteredSelectedRowModel().rows.length;
  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const selectionTotalRowCount = rowCount ?? filteredRowCount;
  const footerLabel = showSelectionCount
    ? (i18n?.selectionCount?.(selectedRowCount, selectionTotalRowCount) ??
      `${selectedRowCount} of ${selectionTotalRowCount} Row(s) Selected.`)
    : null;
  let statusContent: React.ReactNode;
  if (isLoading) {
    statusContent = loadingState || i18n?.loading || 'Loading...';
  } else if (error) {
    statusContent = errorState || error.message;
  } else {
    statusContent = emptyState || i18n?.noResults || 'No results.';
  }
  const statusAriaLive = isLoading || error ? 'polite' : undefined;

  function renderVirtualizedBody(): React.JSX.Element {
    return (
      <TableBody
        style={{
          display: 'block',
          position: 'relative',
          height: `${rowVirtualizer.getTotalSize() + headerOffset}px`,
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          const rowIsGroup = isGroupRow(row);
          const visibleCells = row.getVisibleCells();
          const visibleColumnCount = visibleCells.length;
          const responsiveStickySlotsById = getResponsiveStickySlotsById({
            enabled: responsiveColumns,
            getId: (item) => item.column.id,
            getResponsive: (item) => item.column.columnDef.meta?.responsive,
            items: visibleCells,
            stickyFirstColumn,
            stickyLastColumn,
          });

          if (rowIsGroup && renderGroupHeader) {
            return (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                ref={(node) => rowVirtualizer.measureElement(node)}
                data-index={virtualRow.index}
                className={cn(
                  dataTableRowVariants({
                    dividers,
                    density,
                    isEven: virtualRow.index % 2 === 1,
                    isSelected: row.getIsSelected(),
                  }),
                  'bg-muted',
                  classNames?.row,
                )}
                style={getVirtualRowStyle(virtualRow.start, headerOffset)}
              >
                <DataTableGroupHeader
                  row={row}
                  visibleColumnCount={visibleColumnCount}
                  customHeader={renderGroupHeader}
                />
              </TableRow>
            );
          }

          return (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && 'selected'}
              aria-selected={row.getIsSelected() ? 'true' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick ? (e) => handleRowKeyDown(row, e) : undefined
              }
              ref={(node) => rowVirtualizer.measureElement(node)}
              data-index={virtualRow.index}
              className={cn(
                dataTableRowVariants({
                  dividers,
                  density,
                  isEven: virtualRow.index % 2 === 1,
                  isSelected: row.getIsSelected(),
                }),
                rowIsGroup && 'bg-muted',
                onRowClick &&
                  'cursor-pointer hover:bg-muted focus-visible:bg-muted focus-visible:outline-none active:bg-accent/50 motion-reduce:active:bg-muted',
                classNames?.row,
              )}
              style={getVirtualRowStyle(virtualRow.start, headerOffset)}
            >
              {visibleCells.map((cell, cellIndex) => {
                const isFirstColumn = cellIndex === 0;
                const isLastColumn = cellIndex === visibleColumnCount - 1;
                const responsiveStickySlots = responsiveStickySlotsById.get(
                  cell.column.id,
                );
                const stickyClass = responsiveColumns
                  ? getResponsiveStickyClass({
                      backgroundClass: row.getIsSelected()
                        ? 'bg-muted dark:bg-accent'
                        : 'bg-background dark:bg-card',
                      firstSlots: responsiveStickySlots?.firstSlots ?? [],
                      lastSlots: responsiveStickySlots?.lastSlots ?? [],
                    })
                  : getStickyCellClass({
                      stickyFirstColumn,
                      stickyLastColumn,
                      isFirstColumn,
                      isLastColumn,
                      isSelected: row.getIsSelected(),
                    });

                return (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      dataTableCellVariants({ density }),
                      classNames?.cell,
                      cell.column.columnDef.meta?.cellClassName,
                      getResponsiveColumnClass(
                        cell.column.columnDef.meta?.responsive,
                        responsiveColumns,
                      ),
                      stickyClass,
                    )}
                  >
                    {rowIsGroup
                      ? renderCellContent(cell, row)
                      : flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    );
  }

  function renderStandardBody(): React.JSX.Element {
    let bodyContent: React.ReactNode;

    if (!rows.length) {
      bodyContent = (
        <TableRow>
          <TableCell
            colSpan={table.getAllColumns().length}
            className={cn(
              dataTableCellVariants({ density }),
              'h-24 text-center text-muted-foreground',
            )}
            aria-live={statusAriaLive}
          >
            {statusContent}
          </TableCell>
        </TableRow>
      );
    } else if (renderRow) {
      bodyContent = rows.map((row) => renderRow(row));
    } else {
      bodyContent = rows.map((row, index) => {
        const rowIsGroup = isGroupRow(row);
        const visibleCells = row.getVisibleCells();
        const visibleColumnCount = visibleCells.length;
        const responsiveStickySlotsById = getResponsiveStickySlotsById({
          enabled: responsiveColumns,
          getId: (item) => item.column.id,
          getResponsive: (item) => item.column.columnDef.meta?.responsive,
          items: visibleCells,
          stickyFirstColumn,
          stickyLastColumn,
        });

        if (rowIsGroup && renderGroupHeader) {
          return (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && 'selected'}
              className={cn(
                dataTableRowVariants({
                  dividers,
                  density,
                  isEven: index % 2 === 1,
                  isSelected: row.getIsSelected(),
                }),
                'bg-muted',
                classNames?.row,
              )}
            >
              <DataTableGroupHeader
                row={row}
                visibleColumnCount={visibleColumnCount}
                customHeader={renderGroupHeader}
              />
            </TableRow>
          );
        }

        return (
          <TableRow
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
            aria-selected={row.getIsSelected() ? 'true' : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={onRowClick ? (e) => handleRowKeyDown(row, e) : undefined}
            className={cn(
              dataTableRowVariants({
                dividers,
                density,
                isEven: index % 2 === 1,
                isSelected: row.getIsSelected(),
              }),
              rowIsGroup && 'bg-muted',
              onRowClick &&
                'cursor-pointer hover:bg-muted focus-visible:bg-muted focus-visible:outline-none active:bg-accent/50 motion-reduce:active:bg-muted',
              classNames?.row,
            )}
          >
            {visibleCells.map((cell, cellIndex) => {
              const isFirstColumn = cellIndex === 0;
              const isLastColumn = cellIndex === visibleColumnCount - 1;
              const responsiveStickySlots = responsiveStickySlotsById.get(
                cell.column.id,
              );
              const stickyClass = responsiveColumns
                ? getResponsiveStickyClass({
                    backgroundClass: row.getIsSelected()
                      ? 'bg-muted dark:bg-accent'
                      : 'bg-background dark:bg-card',
                    firstSlots: responsiveStickySlots?.firstSlots ?? [],
                    lastSlots: responsiveStickySlots?.lastSlots ?? [],
                  })
                : getStickyCellClass({
                    stickyFirstColumn,
                    stickyLastColumn,
                    isFirstColumn,
                    isLastColumn,
                    isSelected: row.getIsSelected(),
                  });

              return (
                <TableCell
                  key={cell.id}
                  className={cn(
                    dataTableCellVariants({ density }),
                    classNames?.cell,
                    cell.column.columnDef.meta?.cellClassName,
                    getResponsiveColumnClass(
                      cell.column.columnDef.meta?.responsive,
                      responsiveColumns,
                    ),
                    stickyClass,
                  )}
                >
                  {rowIsGroup
                    ? renderCellContent(cell, row)
                    : flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
          </TableRow>
        );
      });
    }

    return <TableBody>{bodyContent}</TableBody>;
  }

  let tableBody: React.ReactNode;
  if (renderBody) {
    tableBody = renderBody(table);
  } else if (isVirtualizationEnabled && rows.length > 0) {
    tableBody = renderVirtualizedBody();
  } else {
    tableBody = renderStandardBody();
  }

  const tableContainer = (
    <div
      ref={tableContainerRef}
      className={cn(
        dataTableContainerVariants({
          rounded: shouldRenderComposite ? 'none' : rounded,
          bordered: shouldRenderComposite ? false : bordered,
        }),
        maxHeight ? 'overflow-auto' : 'overflow-x-auto',
      )}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <Table
        disableOverflow={
          !!(maxHeight || stickyHeader || stickyFirstColumn || stickyLastColumn)
        }
        className={cn(
          dataTableVariants({ dividers, density }),
          classNames?.table,
        )}
      >
        {caption && <caption className="sr-only">{caption}</caption>}
        <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10')}>
          {table.getHeaderGroups().map((headerGroup) => {
            const responsiveStickySlotsById = getResponsiveStickySlotsById({
              enabled: responsiveColumns,
              getId: (item) => item.column.id,
              getResponsive: (item) => item.column.columnDef.meta?.responsive,
              items: headerGroup.headers,
              stickyFirstColumn,
              stickyLastColumn,
            });

            return (
              <TableRow
                key={headerGroup.id}
                className={cn(
                  'border-b border-border bg-muted hover:bg-muted',
                  classNames?.headerRow,
                )}
              >
                {headerGroup.headers.map((header, headerIndex) => {
                  const isFirstColumn = headerIndex === 0;
                  const isLastColumn =
                    headerIndex === headerGroup.headers.length - 1;
                  const isSticky =
                    (stickyFirstColumn && isFirstColumn) ||
                    (stickyLastColumn && isLastColumn);
                  const responsiveStickySlots = responsiveStickySlotsById.get(
                    header.column.id,
                  );
                  const responsiveHeaderStickyClass = responsiveColumns
                    ? getResponsiveStickyClass({
                        backgroundClass: 'bg-muted',
                        firstSlots: responsiveStickySlots?.firstSlots ?? [],
                        lastSlots: responsiveStickySlots?.lastSlots ?? [],
                        zIndexClass: stickyHeader ? 'z-30' : 'z-20',
                      })
                    : undefined;

                  const ariaSort = getAriaSort(header.column.getIsSorted());
                  const headerDefinition = header.column.columnDef.header;
                  const isTextHeader = typeof headerDefinition === 'string';

                  let headerContent: React.ReactNode = null;
                  if (!header.isPlaceholder && isTextHeader) {
                    headerContent = (
                      <SortableHeader column={header.column}>
                        {headerDefinition}
                      </SortableHeader>
                    );
                  } else if (!header.isPlaceholder) {
                    headerContent = flexRender(
                      headerDefinition,
                      header.getContext(),
                    );
                  }

                  return (
                    <TableHead
                      key={header.id}
                      scope="col"
                      style={{
                        width: getHeaderCellWidth(header.getSize()),
                      }}
                      className={cn(
                        dataTableHeaderCellVariants({ density }),
                        'text-start',
                        classNames?.headerCell,
                        header.column.columnDef.meta?.headerClassName,
                        getResponsiveColumnClass(
                          header.column.columnDef.meta?.responsive,
                          responsiveColumns,
                        ),
                        responsiveColumns
                          ? responsiveHeaderStickyClass
                          : [
                              stickyFirstColumn &&
                                isFirstColumn &&
                                'sticky start-0 z-20 bg-muted',
                              stickyLastColumn &&
                                isLastColumn &&
                                'sticky end-0 z-20 bg-muted',
                              stickyHeader && isSticky && 'z-30',
                            ],
                      )}
                      aria-sort={ariaSort}
                    >
                      {headerContent}
                    </TableHead>
                  );
                })}
              </TableRow>
            );
          })}
        </TableHeader>
        {tableBody}
      </Table>
    </div>
  );

  const tableFooter = hasFooter ? (
    <div
      data-slot="data-table-footer"
      className={cn(
        'flex min-h-[68px] flex-wrap items-center gap-4 border-t border-border-subtle px-6 py-3',
        footerLabel ? 'justify-between' : 'justify-end',
      )}
    >
      {footerLabel && (
        <span className="text-sm text-muted-foreground">{footerLabel}</span>
      )}
      {showPagination && (
        <Pagination
          type={paginationType}
          shape={paginationShape}
          size={paginationSize}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => table.setPageIndex(page - 1)}
          showPageNumbers={paginationShowPageNumbers}
          aria-label={paginationLabel}
          className={cn(
            'min-w-0 max-w-full [&>div]:max-w-full [&>div]:flex-wrap [&>div]:gap-y-2',
            footerLabel && 'w-auto justify-end',
          )}
        />
      )}
    </div>
  ) : null;

  return (
    <div data-slot="data-table" className={cn('@container', className)}>
      {renderControls && (
        <div className="mb-4">
          <DataTableControls table={table} customControls={renderControls} />
        </div>
      )}

      {shouldRenderComposite ? (
        <div
          data-slot="data-table-composite"
          className={cn(
            dataTableContainerVariants({ rounded, bordered }),
            'overflow-hidden',
          )}
        >
          {children}
          {tableContainer}
          {tableFooter}
        </div>
      ) : (
        tableContainer
      )}
    </div>
  );
}

DataTable.displayName = 'DataTable';

/* ============================================
   Exports
   ============================================ */

export type {
  DataTableActionsCellProps,
  DataTableAvatarCellProps,
  DataTableBadgeItem,
  DataTableBadgesCellProps,
  DataTableCellAction,
  DataTableCellRootProps,
  DataTableCellTone,
  DataTableProgressCellProps,
  DataTableRatingCellProps,
  DataTableSparklineCellProps,
  DataTableSwitchCellProps,
  DataTableTextCellProps,
  DataTableTrendCellProps,
  DataTableTrendSentiment,
} from './DataTableCell';
export {
  DataTableActionsCell,
  DataTableAvatarCell,
  DataTableBadgesCell,
  DataTableCell,
  DataTableCellRoot,
  DataTableProgressCell,
  DataTableRatingCell,
  DataTableSparklineCell,
  DataTableSwitchCell,
  DataTableTextCell,
  DataTableTrendCell,
} from './DataTableCell';
export type {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  GroupingState,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  VisibilityState,
};
export {
  DataTable,
  dataTableCellVariants,
  dataTableContainerVariants,
  dataTableHeaderCellVariants,
  dataTableRowVariants,
  dataTableVariants,
};
